import { useEffect, useState } from 'react';
import { Check, Copy, ExternalLink, KeyRound } from 'lucide-react';
import { useSandboxImpersonate } from '@/services/mutations/sandbox';
import { SandboxModal, SandboxNotice } from './SandboxModal';
import { formatTime } from '../sandboxLabels';

export interface ImpersonationTarget {
	kind: Sandbox.ImpersonationKind;
	id: string;
	label: string;
}

interface Props {
	scenarioId: string;
	target: ImpersonationTarget | null;
	onClose: () => void;
}

/**
 * "Entrar como": gera um link de uso único (5 min). O cookie de sessão é o
 * mesmo para admin/usuário/profissional no mesmo navegador, então abrir o link
 * na mesma janela derruba a sessão do admin — por isso a recomendação de
 * janela anônima.
 */
const SandboxImpersonateModal = ({ scenarioId, target, onClose }: Props) => {
	const [result, setResult] = useState<Sandbox.Impersonation | null>(null);
	const [copied, setCopied] = useState(false);
	const { mutate, isPending } = useSandboxImpersonate(scenarioId, setResult);

	useEffect(() => {
		setResult(null);
		setCopied(false);
		if (target) mutate({ kind: target.kind, id: target.id });
		// mutate é estável (react-query); só reage à troca de alvo.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [target?.id, target?.kind]);

	const copy = async () => {
		if (!result) return;
		try {
			await navigator.clipboard.writeText(result.url);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// clipboard indisponível: o link continua visível para seleção manual
		}
	};

	const roleLabel = target?.kind === 'USER' ? 'usuário' : 'profissional';

	return (
		<SandboxModal
			isOpen={Boolean(target)}
			onClose={onClose}
			title={`Entrar como ${target?.label ?? ''}`}
			description={`Link de uso único para abrir a plataforma logado como este ${roleLabel}.`}
			icon={<KeyRound className="h-4 w-4" />}
		>
			<div className="space-y-4 py-2">
				<SandboxNotice tone="warning">
					Abra o link em uma <strong>janela anônima</strong> (ou outro
					navegador). Na mesma janela do admin ele substitui a sua
					sessão e você precisará entrar no admin de novo.
				</SandboxNotice>

				{isPending || !result ? (
					<p className="text-sm text-stone-400">Gerando link...</p>
				) : (
					<>
						<div className="flex items-center gap-2 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
							<code className="min-w-0 flex-1 truncate text-xs text-stone-600">
								{result.url}
							</code>
							<button
								type="button"
								onClick={copy}
								className="inline-flex shrink-0 items-center gap-1 rounded-full bg-stone-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-stone-800"
							>
								<span className="flex h-3.5 w-3.5 items-center justify-center">
									{copied ? (
										<Check className="h-3.5 w-3.5" />
									) : (
										<Copy className="h-3.5 w-3.5" />
									)}
								</span>
								<span>{copied ? 'Copiado' : 'Copiar'}</span>
							</button>
						</div>
						<p className="text-xs text-stone-400">
							Válido até {formatTime(result.expires_at)}. Depois
							de aberto uma vez, deixa de funcionar.
						</p>
						<a
							href={result.url}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-700 transition-colors hover:border-stone-900 hover:text-stone-900"
						>
							<ExternalLink className="h-3.5 w-3.5" />
							Abrir nesta janela (sai do admin)
						</a>
					</>
				)}
			</div>
		</SandboxModal>
	);
};

export { SandboxImpersonateModal };
